import {
  Lightning,
  GitBranch,
  Bell,
  Repeat,
  ArrowRight,
  CheckCircle,
} from "@phosphor-icons/react";

const steps = [
  {
    icon: Lightning,
    title: "Trigger",
    description:
      "A new task is created, a deadline changes, or a status updates.",
    color: "bg-primary-500",
    borderColor: "border-primary-500",
  },
  {
    icon: GitBranch,
    title: "Condition",
    description:
      "AI evaluates rules: priority, team capacity, dependencies, and custom logic.",
    color: "bg-[#3B7AE0]",
    borderColor: "border-[#3B7AE0]",
  },
  {
    icon: Bell,
    title: "Action",
    description:
      "Auto-assign, notify stakeholders, update boards, or escalate to managers.",
    color: "bg-emerald-500",
    borderColor: "border-emerald-500",
  },
  {
    icon: Repeat,
    title: "Learn",
    description:
      "The system learns from outcomes and refines automation rules over time.",
    color: "bg-blue-500",
    borderColor: "border-blue-500",
  },
];

const automationExamples = [
  "Auto-assign bug reports to on-call engineers",
  "Escalate overdue tasks after 48 hours",
  "Notify stakeholders when milestones complete",
  "Re-balance sprint load when someone goes OOO",
  "Create follow-up tasks from resolved tickets",
];

export function WorkflowAutomationSection() {
  return (
    <section id="workflow" className="relative py-24 lg:py-32">
      <div className="absolute inset-0 hex-grid-bg" />

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 dark:border-primary-500/30 bg-primary-500/5 dark:bg-primary-500/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-primary-500 dark:text-primary-400 uppercase">
            <GitBranch size={14} weight="bold" />
            Workflow Automation
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl lg:text-5xl">
            Automate the work <span className="gradient-text">about work</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
            Build intelligent workflows that learn and adapt. No code required.
          </p>
        </div>

        {/* Pipeline visualization */}
        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <div key={step.title} className="relative">
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 -right-2 z-10">
                  <ArrowRight
                    size={16}
                    className="text-slate-300 dark:text-slate-700"
                  />
                </div>
              )}

              <div className="card-snap group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-6 text-center h-full">
                {/* Step number */}
                <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full border-2 border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-400 dark:text-slate-500 group-hover:border-primary-500 group-hover:text-primary-500 transition-colors duration-200">
                  {i + 1}
                </div>

                <div
                  className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl ${step.color}/10`}
                >
                  <step.icon
                    size={24}
                    weight="duotone"
                    className={`${step.color.replace("bg-", "text-")}`}
                  />
                </div>

                <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Examples list */}
        <div className="mt-16 mx-auto max-w-2xl rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-8">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">
            Popular automations teams build with Opero
          </h3>
          <div className="space-y-3">
            {automationExamples.map((example) => (
              <div key={example} className="flex items-center gap-3">
                <CheckCircle
                  size={18}
                  weight="fill"
                  className="text-primary-500 shrink-0"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {example}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
